import { formatMoney } from '../../../utils/money';
import { formatDateCa } from '../legal-board/legalBoardUi';

/**
 * The words of the calendar once the colla is declared: what each deadline is
 * called, the sentence under it and the header line. Pure, so the
 * tab and its tests read the same.
 */

export const ROW_TITLES = {
    model182: (year) => `Model 182 de ${year}`,
    accounts: (year) => `Comptes de ${year} a Justícia`,
    model200: (year) => `Impost de Societats de ${year}`,
    economic_report: (year) => `Memòria econòmica de ${year}`,
};

// What to do, for the header («Ara toca …») and the screen of each deadline
export const TASK_TITLES = {
    model182: (year) => `declarar els donatius de ${year} (Model 182)`,
    accounts: (year) => `presentar els comptes de ${year} a Justícia`,
    model200: (year) => `presentar l’Impost de Societats de ${year}`,
    economic_report: (year) => `presentar la memòria econòmica de ${year}`,
};

export const rowTitle = (item) => ROW_TITLES[item.key](item.fiscalYear);

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);
export const taskTitle = (item) => capitalize(TASK_TITLES[item.key](item.fiscalYear));

// «des de l’1/06/2024» but «des del 10/05/2024»
export const desDe = (date) => {
    const day = Number(String(date || '').slice(8, 10));
    return `${day === 1 || day === 11 ? 'des de l’' : 'des del '}${formatDateCa(date)}`;
};

const until = (date) => {
    const day = Number(String(date || '').slice(8, 10));
    return `${day === 1 || day === 11 ? 'fins a l’' : 'fins al '}${formatDateCa(date)}`;
};

export const daysText = (daysLeft) => {
    if (daysLeft === 0) return 'avui';
    if (daysLeft === 1) return 'demà';
    return `d’aquí a ${daysLeft} dies`;
};

/** The one to do now: the first deadline not presented and not yet past. */
export const nextObligation = (obligations = []) => obligations.find((item) => item.state !== 'done' && item.state !== 'overdue') || null;

const isNext = (item, next) => Boolean(next) && next.key === item.key && next.fiscalYear === item.fiscalYear;

/**
 * What the donors left out of the 182 lack: «3 DNI i 1 codi postal». A DNI
 * without a postal code has no province, which the file asks for.
 */
export const missingDonorData = (totals) => {
    const postal = totals?.withoutPostalCode || 0;
    const dni = (totals?.withoutNif || 0) - postal;
    return [
        dni > 0 ? `${dni} DNI` : null,
        postal > 0 ? `${postal} ${postal === 1 ? 'codi postal' : 'codis postals'}` : null,
    ].filter(Boolean).join(' i ');
};

const donorsLine = (totals) => {
    if (!totals) return 'Descarrega el fitxer i puja’l a l’Agència Tributària.';
    if (totals.donors === 0) return 'Encara no hi ha cap donatiu aquest any.';
    if (totals.withoutNif > 0) {
        return `${totals.withoutNif === 1 ? 'Falta' : 'Falten'} ${missingDonorData(totals)} dels ${totals.donors} donants. Després, descarregues el fitxer i el puges a l’Agència Tributària.`;
    }
    return `${totals.donors} donants · ${formatMoney(totals.amount)} €. Descarrega el fitxer i puja’l a l’Agència Tributària.`;
};

/** One row of the calendar: `line` says where it stands, `tone` colours it. */
export const describeRow = (item, { next, donorTotals = {}, today }) => {
    if (item.state === 'done') {
        return { line: `Presentat el ${formatDateCa(item.doneOn)}`, tone: 'muted', dot: 'done' };
    }
    if (item.state === 'overdue') {
        return { lead: 'El termini ha passat.', line: 'Si ja ho vau presentar, marca-ho.', tone: 'late', dot: 'late' };
    }
    const yearEnded = Number(String(today).slice(0, 4)) > item.fiscalYear;
    if (isNext(item, next)) {
        const lines = {
            model182: donorsLine(donorTotals[item.fiscalYear]),
            accounts: 'Revisa la memòria, passa els ingressos al gestor i presenta-ho a Justícia.',
            model200: 'El prepara el gestor amb els comptes de l’any i es presenta a l’Agència Tributària.',
            economic_report: 'Només cal presentar-la si els ingressos superen 20.000 €.',
        };
        return { line: lines[item.key], tone: 'plain', dot: 'next' };
    }
    const lines = {
        model182: yearEnded ? donorsLine(donorTotals[item.fiscalYear]) : 'Es presenta al gener, amb els donatius de tot l’any.',
        accounts: yearEnded ? 'Revisa la memòria, passa els ingressos al gestor i presenta-ho a Justícia.' : 'Es preparen quan acabi l’any.',
        model200: 'El prepara el gestor amb els comptes.',
        economic_report: 'Només si els ingressos superen 20.000 €.',
    };
    return { line: lines[item.key], tone: 'muted', dot: item.state === 'urgent' || item.state === 'soon' ? 'soon' : '' };
};

/** The header line once declared: what to do now, and what is overdue. */
export const headline = ({ obligations = [], status }) => {
    if (!status.regimeOptedOn) {
        return `Declarada d’utilitat pública ${desDe(status.grantedOn)}. Falta el Model 036 perquè els donatius desgravin.`;
    }
    const next = nextObligation(obligations);
    const overdue = obligations.filter((item) => item.state === 'overdue');
    const now = next ? `Ara toca ${TASK_TITLES[next.key](next.fiscalYear)}, ${until(next.deadline)}.` : 'No hi ha res pendent.';
    const late = overdue.length === 0 ? ''
        : overdue.length === 1 ? ` ${rowTitle(overdue[0])}: el termini ha passat.`
            : ` Hi ha ${overdue.length} terminis passats.`;
    return `${now}${late}`;
};
